import { useContext, useState } from "react";
import { Pencil, Trash2, Plus } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { trpc, RouterOutputs } from "@/trpc";
import MerchantContext from "../../contexts/MerchantContext";
import MainSectionSection from "../../components/MainSectionSection";
import InboundCampaignRuleModal from "../../components/settings/InboundCampaignRuleModal";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useIsMobile } from "@/hooks/useMediaQuery";

type CampaignRule = RouterOutputs["inboundCampaignRules"]["list"][number];

const InboundCampaignRulesContainer = () => {
  const { merchantId, merchantType } = useContext(MerchantContext);
  const isMobile = useIsMobile();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<CampaignRule | null>(null);
  const [ruleToDelete, setRuleToDelete] = useState<CampaignRule | null>(null);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  const rules = trpc.inboundCampaignRules.list.useQuery(
    { merchantId },
    { enabled: !!merchantId }
  );

  const projects = trpc.projects.list.useQuery(
    { merchantId },
    { enabled: !!merchantId && merchantType === "development" }
  );

  const createRule = trpc.inboundCampaignRules.create.useMutation({
    onSuccess: () => {
      rules.refetch();
      toast.success("Campana creada exitosamente");
    },
    onError: (error) => {
      toast.error(error.message || "Error al crear la campana");
    },
  });

  const updateRule = trpc.inboundCampaignRules.update.useMutation({
    onSuccess: () => {
      rules.refetch();
      toast.success("Campana actualizada exitosamente");
    },
    onError: (error) => {
      toast.error(error.message || "Error al actualizar la campana");
    },
  });

  const removeRule = trpc.inboundCampaignRules.remove.useMutation({
    onSuccess: () => {
      rules.refetch();
      toast.success("Campana eliminada exitosamente");
    },
    onError: (error) => {
      toast.error(error.message || "Error al eliminar la campana");
    },
  });

  const handleCreate = async (data: { name: string; messagePattern: string; projectId?: string }) => {
    await createRule.mutateAsync({ merchantId, ...data });
  };

  const handleEdit = async (data: { name: string; messagePattern: string; projectId?: string }) => {
    if (!editingRule) return;
    await updateRule.mutateAsync({ merchantId, id: editingRule.id, ...data });
  };

  const handleToggleActive = async (rule: CampaignRule) => {
    await updateRule.mutateAsync({
      merchantId,
      id: rule.id,
      isActive: !rule.isActive,
    });
  };

  const handleDeleteClick = (rule: CampaignRule) => {
    setRuleToDelete(rule);
    setIsDeleteOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!ruleToDelete) return;
    await removeRule.mutateAsync({ merchantId, id: ruleToDelete.id });
    setRuleToDelete(null);
    setIsDeleteOpen(false);
  };

  const handleOpenCreate = () => {
    setEditingRule(null);
    setIsModalOpen(true);
  };

  const handleOpenEdit = (rule: CampaignRule) => {
    setEditingRule(rule);
    setIsModalOpen(true);
  };

  const rulesData = rules.data ?? [];

  return (
    <MainSectionSection>
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-gray-800 md:text-2xl">Pauta</h1>
        <Button onClick={handleOpenCreate} size="sm">
          <Plus className="mr-1 size-4" />
          Asociar Pauta
        </Button>
      </div>

      {/* Content */}
      {rules.isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Spinner size="xl" />
        </div>
      ) : rulesData.length === 0 ? (
        <div className="flex min-h-[200px] items-center justify-center p-8">
          <div className="flex flex-col items-center gap-2">
            <p className="text-lg text-gray-500">No hay campanas configuradas</p>
            <p className="text-sm text-gray-400">
              Crea una campana para segmentar tus leads de pauta.
            </p>
          </div>
        </div>
      ) : isMobile ? (
        /* Mobile: card layout */
        <div className="flex flex-col gap-3">
          {rulesData.map((rule) => (
            <div
              key={rule.id}
              className="rounded-lg border border-gray-200 bg-white p-4"
            >
              <div className="flex items-start justify-between">
                <div className="flex flex-col gap-1">
                  <p className="font-semibold text-gray-800">{rule.name}</p>
                  <p className="text-sm text-gray-500">{rule.messagePattern}</p>
                  {merchantType === "development" && rule.projectId && (
                    <p className="text-xs text-gray-400">
                      {projects.data?.find((p) => p.id === rule.projectId)?.name || ""}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleOpenEdit(rule)}
                    className="size-8"
                  >
                    <Pencil className="size-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDeleteClick(rule)}
                    disabled={removeRule.isLoading}
                    className="size-8 text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="size-4" />
                  </Button>
                </div>
              </div>
              <div className="mt-3 flex items-center gap-2">
                <button
                  type="button"
                  role="switch"
                  aria-checked={rule.isActive}
                  onClick={() => handleToggleActive(rule)}
                  disabled={updateRule.isLoading}
                  className={cn(
                    "relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors disabled:cursor-not-allowed disabled:opacity-50",
                    rule.isActive ? "bg-palomma-primary" : "bg-gray-300"
                  )}
                >
                  <span
                    className={cn(
                      "pointer-events-none block size-4 rounded-full bg-white shadow-sm transition-transform",
                      rule.isActive ? "translate-x-4" : "translate-x-0"
                    )}
                  />
                </button>
                <p className={cn("text-sm", rule.isActive ? "text-green-600" : "text-gray-500")}>
                  {rule.isActive ? "Activa" : "Inactiva"}
                </p>
              </div>
            </div>
          ))}
        </div>
      ) : (
        /* Desktop: table layout */
        <div className="rounded-lg border border-gray-200 bg-white">
          {/* Header */}
          <div className="flex border-b border-gray-200 bg-gray-50 px-4 py-3 text-sm font-semibold text-gray-600">
            <div style={{ flex: "0 0 25%" }}>Nombre</div>
            <div style={{ flex: merchantType === "development" ? "0 0 25%" : "0 0 35%" }}>Patron de mensaje</div>
            {merchantType === "development" && (
              <div style={{ flex: "0 0 20%" }}>Proyecto</div>
            )}
            <div style={{ flex: "0 0 15%" }}>Estado</div>
            <div style={{ flex: "0 0 15%" }}>Acciones</div>
          </div>
          {/* Rows */}
          {rulesData.map((rule) => (
            <div
              key={rule.id}
              className="flex items-center border-b border-gray-100 px-4 py-3 last:border-b-0"
            >
              <div style={{ flex: "0 0 25%" }} className="font-medium text-gray-800">
                {rule.name}
              </div>
              <div style={{ flex: merchantType === "development" ? "0 0 25%" : "0 0 35%" }} className="text-sm text-gray-600">
                {rule.messagePattern}
              </div>
              {merchantType === "development" && (
                <div style={{ flex: "0 0 20%" }} className="text-sm text-gray-600">
                  {projects.data?.find((p) => p.id === rule.projectId)?.name || "—"}
                </div>
              )}
              <div style={{ flex: "0 0 15%" }} className="flex items-center gap-2">
                <button
                  type="button"
                  role="switch"
                  aria-checked={rule.isActive}
                  onClick={() => handleToggleActive(rule)}
                  disabled={updateRule.isLoading}
                  className={cn(
                    "relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors disabled:cursor-not-allowed disabled:opacity-50",
                    rule.isActive ? "bg-palomma-primary" : "bg-gray-300"
                  )}
                >
                  <span
                    className={cn(
                      "pointer-events-none block size-4 rounded-full bg-white shadow-sm transition-transform",
                      rule.isActive ? "translate-x-4" : "translate-x-0"
                    )}
                  />
                </button>
                <p className={cn("text-sm", rule.isActive ? "text-green-600" : "text-gray-500")}>
                  {rule.isActive ? "Activa" : "Inactiva"}
                </p>
              </div>
              <div style={{ flex: "0 0 15%" }} className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleOpenEdit(rule)}
                  className="size-8"
                >
                  <Pencil className="size-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleDeleteClick(rule)}
                  disabled={removeRule.isLoading}
                  className="size-8 text-red-600 hover:text-red-700"
                >
                  <Trash2 className="size-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      <InboundCampaignRuleModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingRule(null);
        }}
        onSubmit={editingRule ? handleEdit : handleCreate}
        initialData={editingRule}
        merchantType={merchantType}
        projects={projects.data}
      />

      {/* Delete Confirmation */}
      <AlertDialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar campana</AlertDialogTitle>
            <AlertDialogDescription>
              Estas seguro de eliminar la campana <strong>{ruleToDelete?.name}</strong>? Esta accion
              no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-red-500 text-white hover:bg-red-600"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </MainSectionSection>
  );
};

export default InboundCampaignRulesContainer;
